import { useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAppSelector, useAppDispatch } from '@/features/hooks';
import { fetchTemplates } from '@/features/templates/templatesSlice';
import type { TransactionTemplate } from '@/lib/types';

interface TemplatePickerProps {
  value: string | null;
  onChange: (templateId: string | null, template?: TransactionTemplate) => void;
  disabled?: boolean;
}

export function TemplatePicker({ value, onChange, disabled }: TemplatePickerProps) {
  const dispatch = useAppDispatch();
  const templates = useAppSelector((state) => state.templates.templates);

  useEffect(() => {
    if (templates.length === 0) {
      dispatch(fetchTemplates());
    }
  }, [dispatch, templates.length]);

  const handleChange = (val: string) => {
    if (val === '__none__') {
      onChange(null);
    } else {
      const template = templates.find((t) => t._id === val);
      onChange(val, template);
    }
  };

  return (
    <Select
      value={value || '__none__'}
      onValueChange={handleChange}
      disabled={disabled}
    >
      <SelectTrigger data-testid="template-picker-trigger">
        <SelectValue placeholder="Select a template" />
      </SelectTrigger>
      <SelectContent data-testid="template-picker">
        <SelectItem value="__none__">None</SelectItem>
        {templates.map((t) => (
          <SelectItem key={t._id} value={t._id}>
            {t.emoji ? `${t.emoji} ${t.name}` : t.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
